import React from "react";

/**
 * Reusable Decorative Grid Lines Component
 *
 * Structure:
 * - 5 horizontal rows
 * - 12 columns per row (Half width cell on col 1 and col 12)
 * - Row 2 & 3: Two 2x2 empty boxes (Box 1 at cols 2-3, Box 2 at cols 4-5)
 * - Row 4 & 5: Normal grid lines
 *
 * @param {string} variant - Optional layout variant name
 * @param {string} label - Optional target node label
 */
const DecorativeLines = ({ variant = "default", label = "" }) => {
    const rows = [1, 2, 3, 4, 5];
    const cols = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    /**
     * Determines if the vertical right border of column `colIndex` should be omitted
     */
    const isVerticalLineHidden = (rowIndex, colIndex) => {
        // Row 2 & 3:
        // Box 1 (2x2): Col 2 & 3 (omits vertical line at colIndex 2)
        // Box 2 (2x2): Col 4 & 5 (omits vertical line at colIndex 4)
        if (rowIndex === 2 || rowIndex === 3) {
            if (colIndex === 2 || colIndex === 4) {
                return true;
            }
        }
        return false;
    };

    /**
     * Determines if the horizontal bottom border of row `rowIndex` should be omitted
     */
    const isBottomLineHidden = (rowIndex, colIndex) => {
        // Row 2:
        // Box 1 (2x2): Col 2 & 3 (omits horizontal line between Row 2 & 3)
        // Box 2 (2x2): Col 4 & 5 (omits horizontal line between Row 2 & 3)
        if (rowIndex === 2) {
            if (
                colIndex === 2 ||
                colIndex === 3 ||
                colIndex === 4 ||
                colIndex === 5
            ) {
                return true;
            }
        }
        return false;
    };

    return (
        <div className={`DecorativeLines DecorativeLines--${variant}`}>
            <div className="DecorativeLines__grid">
                {rows.map((rowIndex) => (
                    <div
                        key={`row-${rowIndex}`}
                        className={`DecorativeLines__row DecorativeLines__row--${rowIndex}`}
                    >
                        {cols.map((colIndex) => {
                            const hideRightBorder = isVerticalLineHidden(
                                rowIndex,
                                colIndex,
                            );
                            const hideBottomBorder = isBottomLineHidden(
                                rowIndex,
                                colIndex,
                            );
                            const isHalfCol = colIndex === 1 || colIndex === 12;
                            return (
                                <div
                                    key={`cell-${rowIndex}-${colIndex}`}
                                    className={`DecorativeLines__cell ${
                                        isHalfCol
                                            ? "DecorativeLines__cell--half"
                                            : ""
                                    } ${
                                        hideRightBorder
                                            ? "DecorativeLines__cell--no-v-line"
                                            : ""
                                    } ${
                                        hideBottomBorder
                                            ? "DecorativeLines__cell--no-h-line"
                                            : ""
                                    }`}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DecorativeLines;
